import React, { useCallback, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Check } from "lucide-react";
import { DatePicker, Select } from "../common";
import Modal2, { Modal2Footer, Modal2Button } from "../common/Modal2";
import { useGetUsersForFilterQuery } from "../../store/apis/user.api";
import { useGetProjectDropdownQuery } from "../../store/apis/resource-allocation/project-management.api";
import { useGetConfigsByCategoryCodeQuery } from "../../store/apis/masterConfig.api";
import { IUpsertAllocationRequest, useUpsertAllocationMutation } from "../../store/apis/resource-allocation/resource-allocation.api";

dayjs.extend(utc);
dayjs.extend(timezone);

interface ResourceAllocationModalProps {
  mode?: "projectToUser" | "userToProject" | "both";
  projectId?: string;
  userId?: string;
  onClose: () => void;
  onSave: (resource: any) => void;
}

const formatOption = (item: any) => ({
  label: item?.displayName,
  value: item?.id ?? item?._id,
});

const AllocationSlider: React.FC<{
  value: number;
  onChange: (value: number) => void;
}> = ({ value, onChange }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const clampToInteger = (pct: number) => Math.min(100, Math.max(1, Math.round(pct)));

  const pctFromEvent = useCallback((e: React.MouseEvent | MouseEvent) => {
    if (!trackRef.current) return value;
    const rect = trackRef.current.getBoundingClientRect();
    const x = ("clientX" in e ? e.clientX : 0) - rect.left;
    return clampToInteger((x / rect.width) * 100);
  }, [value]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    setDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    onChange(pctFromEvent(e));
  }, [onChange, pctFromEvent]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging) return;
    onChange(pctFromEvent(e));
  }, [dragging, onChange, pctFromEvent]);

  const handlePointerUp = useCallback(() => {
    setDragging(false);
  }, []);

  return (
    <div>
      <div className="mb-2 text-[12.5px] font-semibold text-[#374151]">
        Allocation —{" "}
        <span className="text-[#6D28D9] font-extrabold">{value || 0}%</span>
      </div>
      <div
        ref={trackRef}
        className="relative h-2 rounded-full bg-[#F2F0F8] cursor-pointer select-none touch-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {/* Fill */}
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-[#6D28D9] pointer-events-none"
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
        {/* Thumb */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-white border-[3px] border-[#6D28D9] shadow-[0_2px_6px_rgba(26,23,48,.2)] pointer-events-none"
          style={{ left: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
    </div>
  );
};

const ResourceAllocationModal: React.FC<ResourceAllocationModalProps> = ({
  mode = "both",
  projectId,
  userId,
  onClose,
  onSave,
}) => {
  const [upsertAllocation, { isLoading: isSaving }] = useUpsertAllocationMutation();

  const { data: rawEmployees, isFetching } = useGetUsersForFilterQuery(
    { includeInactive: false },
  );

  const { data: projectOptions = [] } = useGetProjectDropdownQuery(
    undefined,
    { skip: mode === "projectToUser" }
  );

  const { data: rawRoles } = useGetConfigsByCategoryCodeQuery({ categoryCode: "project_role" });
  const { data: rawGrades } = useGetConfigsByCategoryCodeQuery({ categoryCode: "allocation_grade" });

  const employeeOptions = useMemo(() => {
    const list = Array.isArray(rawEmployees) ? rawEmployees : [];
    return list.map((u: any) => ({
      label: u.fullName,
      value: u.id,
    }));
  }, [rawEmployees]);

  const roleOptions = useMemo(() => {
    const list = Array.isArray(rawRoles?.data) ? rawRoles.data : [];
    const items = list.map(formatOption);
    return Array.from(new Map(items.map((i: any) => [i.value, i])).values());
  }, [rawRoles]);

  const gradeOptions = useMemo(() => {
    const list = Array.isArray(rawGrades?.data) ? rawGrades.data : [];
    const items = list.map(formatOption);
    return Array.from(new Map(items.map((i: any) => [i.value, i])).values());
  }, [rawGrades]);

  const [formData, setFormData] = useState({
    userId: userId ?? "",
    projectId: projectId ?? "",
    allocationPercentage: 0,
    projectRole: "",
    startDate: "",
    endDate: "",
    grade: "",
  });

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const fieldLabels: Record<string, string> = {
    userId: "Employee",
    projectId: "Project",
    allocationPercentage: "Allocation Percentage",
    projectRole: "Project Role",
    startDate: "Allocation Start Date",
    endDate: "Allocation End Date",
  };

  const showRequiredError = (key: string) => {
    const label = fieldLabels[key] || key;
    toast.error(`${label} is required`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const required =
      mode === "projectToUser"
        ? [
            "userId",
            "allocationPercentage",
            "projectRole",
            "startDate",
            "endDate",
          ]
        : mode === "userToProject"
        ? [
            "projectId",
            "allocationPercentage",
            "projectRole",
            "startDate",
            "endDate",
          ]
        : [
            "userId",
            "projectId",
            "allocationPercentage",
            "projectRole",
            "startDate",
            "endDate",
          ];

    // ---------- Required field validation ----------
    for (let key of required) {
      const value = formData[key as keyof typeof formData];

      if (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "") ||
        (typeof value === "number" && value <= 0)
      ) {
        return showRequiredError(key);
      }
    }

    // ---------- Allocation % validation ----------
    const allocation = Number(formData.allocationPercentage);

    if (
      allocation < 1 ||
      allocation > 100 ||
      !Number.isInteger(allocation)
    ) {
      return toast.error(
        "Allocation % must be between 1 and 100 and must be an integer"
      );
    }

    // ---------- Date validation ----------
    if (dayjs(formData.endDate).isBefore(dayjs(formData.startDate))) {
      return toast.error("End date cannot be before Start date");
    }

    const payload: IUpsertAllocationRequest = {
      userId: mode === "userToProject" ? (userId as string) : (formData.userId as string),
      projectId: mode === "projectToUser" ? (projectId as string) : (formData.projectId as string),
      allocationPercentage: allocation,
      projectRole: formData.projectRole?.toLowerCase() || "",
      startDate: formData.startDate,
      endDate: formData.endDate,
      grade: formData.grade?.toLowerCase() || undefined,
    };

    try {
      const response = await upsertAllocation(payload).unwrap();

      onSave(response.data);
      toast.success(response?.message || "Allocation saved successfully!");
      onClose();
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || "Failed to save allocation";
      toast.error(errorMessage);
      console.error("Allocation Error:", err);
    }
  };

  const formId = "resource-allocation-form";
  const isUserToProject = mode === "userToProject";
  const modalTitle = isUserToProject ? "Assign Project" : "Allocate Resource";

  return (
    <Modal2
      isOpen={true}
      onClose={isFetching ? () => { } : onClose}
      title={modalTitle}
      subtitle="Create an allocation between a resource and a project"
      size="lg"
      loading={isFetching}
      maskClosable={false}
      footer={
        <Modal2Footer>
          <Modal2Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Modal2Button>
          <Modal2Button
            variant="primary"
            type="submit"
            form={formId}
            loading={isSaving}
            icon={<Check className="w-4 h-4" />}
          >
            {isUserToProject ? "Assign" : "Allocate"}
          </Modal2Button>
        </Modal2Footer>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="space-y-5">
        {/* Employee + Project — side-by-side in "both" mode */}
        {mode === "both" && (
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Select Resource"
              value={formData.userId}
              onChange={(v) => handleChange("userId", v)}
              options={employeeOptions}
              required
            />
            <Select
              label="Select Project"
              value={formData.projectId}
              onChange={(v) => handleChange("projectId", v)}
              options={projectOptions}
              required
            />
          </div>
        )}

        {/* Select Employee — shown for projectToUser only */}
        {mode === "projectToUser" && (
          <Select
            label="Select Resource"
            value={formData.userId}
            onChange={(v) => handleChange("userId", v)}
            options={employeeOptions}
            required
          />
        )}

        {/* Select Project — shown for userToProject only */}
        {mode === "userToProject" && (
          <Select
            label="Select Project"
            value={formData.projectId}
            onChange={(v) => handleChange("projectId", v)}
            options={projectOptions}
            required
          />
        )}

        {/* Allocation Percentage — Slider */}
        <AllocationSlider
          value={formData.allocationPercentage}
          onChange={(v) => handleChange("allocationPercentage", v)}
        />

        {/* Project Role */}
        <Select
          label="Project Role"
          value={formData.projectRole}
          onChange={(v) => handleChange("projectRole", v)}
          options={roleOptions}
          placeholder="Select Project Role"
          searchable
          required
        />

        {/* Grade */}
        <Select
          label="Grade"
          value={formData.grade}
          onChange={(v) => handleChange("grade", v)}
          options={gradeOptions}
          placeholder="Select Grade"
          searchable
        />

        {/* Start + End Date — side-by-side */}
        <div className="grid grid-cols-2 gap-4">
          <DatePicker
            label="Start Date"
            value={formData.startDate ? dayjs(formData.startDate) : null}
            onChange={(d) =>
              handleChange("startDate", d?.format("YYYY-MM-DD") || "")
            }
            format="DD/MM/YYYY"
            required
          />
          <DatePicker
            label="End Date"
            value={formData.endDate ? dayjs(formData.endDate) : null}
            onChange={(d) =>
              handleChange("endDate", d?.format("YYYY-MM-DD") || "")
            }
            format="DD/MM/YYYY"
            required
          />
        </div>
      </form>
    </Modal2>
  );
};

export default ResourceAllocationModal;